import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IonButton, IonContent, IonPage, IonSpinner, useIonRouter } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar } from '../components/ui/Avatar';
import { SignInModal } from '../components/auth/SignInModal';
import { useAcceptConnection, useRejectConnection } from '../hooks/useConnections';
import { resolveConnection } from '../services/connectionService';
import type { Connection } from '../types/connection';
import { useAuthStore } from '../store/useAuthStore';
import { useProfileStore } from '../store/useProfileStore';

/**
 * Landing page for the `/c={id}` connection deep link (emails, SMS, push, or a
 * pasted share link). Flow:
 *
 *   1. Unauthenticated → hold the id and pop the sign-in/up modal in place.
 *      Staying on this URL is what preserves the id across auth — no returnTo
 *      plumbing needed. Once `isAuthenticated` flips, we continue.
 *   2. Authenticated → call `GET /connections/resolve/{id}` exactly once
 *      (resolve has server-side side effects — see the service doc), then
 *      render the appropriate state: accept/decline for a pending request to
 *      us, "already connected", a closed notice, or "this is your own link".
 */
export const ConnectionResolvePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('connections');
  const router = useIonRouter();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const myId = useProfileStore((s) => s.activeProfile?.id);

  const accept = useAcceptConnection();
  const reject = useRejectConnection();
  const queryClient = useQueryClient();

  const [signInOpen, setSignInOpen] = useState(true);
  const [outcome, setOutcome] = useState<'accepted' | 'declined' | null>(null);
  const [connection, setConnection] = useState<Connection | null>(null);
  const [resolveErrored, setResolveErrored] = useState(false);

  // Resolve exactly once after auth. We call the service directly and store the
  // result in local state rather than via a React Query mutation: resolve
  // fires during the whoami race (a 401 → token-refresh → retry plus the
  // re-renders that follow), and the mutation observer's result was getting
  // dropped before it reached render. A plain promise → setState is robust
  // against that churn. The ref guard prevents a duplicate call — the
  // forwarded-link case clones a new row on every resolve.
  const resolveFired = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !id || resolveFired.current) return;
    resolveFired.current = true;
    resolveConnection(id)
      .then((data) => {
        setConnection(data);
        // A newly-stamped/cloned row should appear in the Profile tabs.
        queryClient.invalidateQueries({ queryKey: ['connections'] });
      })
      .catch(() => setResolveErrored(true));
  }, [isAuthenticated, id, queryClient]);

  const goConnections = () => router.push('/connections');
  const goHome = () => router.push('/');

  const card = (children: React.ReactNode) => (
    <IonPage>
      <IonContent>
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="w-full max-w-sm rounded-xl bg-card text-card-foreground p-6 shadow-lg">
            {children}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );

  const spinner = (
    <IonPage>
      <IonContent>
        <div className="flex h-full items-center justify-center">
          <IonSpinner name="crescent" />
        </div>
      </IonContent>
    </IonPage>
  );

  const errorCard = card(
    <>
      <h2 className="mb-2 text-lg font-bold text-foreground">{t('resolve.errorTitle')}</h2>
      <p className="mb-4 text-sm text-muted-foreground">{t('resolve.errorBody')}</p>
      <IonButton expand="block" onClick={goHome}>
        {t('resolve.goHome')}
      </IonButton>
    </>,
  );

  // 1. Not signed in — prompt sign-in/up; keep the id by staying on this URL.
  if (!isAuthenticated) {
    return (
      <>
        {card(
          <>
            <h2 className="mb-2 text-lg font-bold text-foreground">
              {t('resolve.signInTitle')}
            </h2>
            <p className="mb-4 text-sm text-muted-foreground">
              {t('resolve.signInBody')}
            </p>
            <IonButton expand="block" onClick={() => setSignInOpen(true)}>
              {t('resolve.signInCta')}
            </IonButton>
          </>,
        )}
        <SignInModal isOpen={signInOpen} onClose={() => setSignInOpen(false)} />
      </>
    );
  }

  // 2. Authenticated. A missing id is a malformed link; a resolve failure is an
  // error — both surface the recoverable error card rather than spinning.
  if (!id || resolveErrored) return errorCard;

  // Wait on the one-shot resolve to settle.
  if (!connection) return spinner;

  const initiator = connection.profile;
  const name = initiator?.displayName || initiator?.email || t('list.someone');

  // For an already-established connection the row may have been initiated by
  // either side, so the "initiator" can be the caller. Name the OTHER party
  // for the already-connected message.
  const otherParty =
    connection.profileId === myId ? connection.connectedProfile : connection.profile;
  const otherName = otherParty?.displayName || otherParty?.email || t('list.someone');

  // Terminal states after the user acts.
  if (outcome === 'accepted') {
    return card(
      <>
        <h2 className="mb-2 text-lg font-bold text-foreground">
          {t('resolve.acceptedTitle')}
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">
          {t('resolve.acceptedBody', { name })}
        </p>
        <IonButton expand="block" onClick={goConnections}>
          {t('resolve.goToConnections')}
        </IonButton>
      </>,
    );
  }
  if (outcome === 'declined') {
    return card(
      <>
        <h2 className="mb-2 text-lg font-bold text-foreground">
          {t('resolve.declinedTitle')}
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">{t('resolve.declinedBody')}</p>
        <IonButton expand="block" onClick={goHome}>
          {t('resolve.goHome')}
        </IonButton>
      </>,
    );
  }

  // Already an established connection. Checked BEFORE the own-link branch:
  // the backend's already-connected guard returns the existing live row, which
  // may have been initiated by either side, so an accepted connection must read
  // as "already connected" (naming the other party) regardless of direction —
  // not as "your own link".
  if (connection.acceptedAt) {
    return card(
      <>
        <h2 className="mb-2 text-lg font-bold text-foreground">
          {t('resolve.alreadyTitle')}
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">
          {t('resolve.alreadyBody', { name: otherName })}
        </p>
        <IonButton expand="block" onClick={goConnections}>
          {t('resolve.goToConnections')}
        </IonButton>
      </>,
    );
  }

  // Caller is the initiator clicking their own (still-pending) link.
  if (connection.profileId === myId) {
    return card(
      <>
        <h2 className="mb-2 text-lg font-bold text-foreground">
          {t('resolve.ownLinkTitle')}
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">{t('resolve.ownLinkBody')}</p>
        <IonButton expand="block" onClick={goConnections}>
          {t('resolve.goToConnections')}
        </IonButton>
      </>,
    );
  }

  // Rejected (e.g. previously declined on another surface).
  if (connection.rejectedAt) {
    return card(
      <>
        <h2 className="mb-2 text-lg font-bold text-foreground">
          {t('resolve.closedTitle')}
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">{t('resolve.closedBody')}</p>
        <IonButton expand="block" onClick={goHome}>
          {t('resolve.goHome')}
        </IonButton>
      </>,
    );
  }

  // Pending request to the caller — accept / decline.
  const isActing = accept.isPending || reject.isPending;
  return card(
    <>
      <div className="mb-3 flex flex-col items-center gap-2 text-center">
        <Avatar src={initiator?.avatarUrl} name={name} size="lg" />
        <h2 className="text-lg font-bold text-foreground">
          {initiator
            ? t('resolve.requestTitle', { name })
            : t('resolve.requestTitleGeneric')}
        </h2>
      </div>
      <p className="mb-4 text-center text-sm text-muted-foreground">
        {t('resolve.requestBody')}
      </p>
      <div className="flex gap-2">
        <IonButton
          expand="block"
          fill="outline"
          color="medium"
          className="flex-1"
          disabled={isActing}
          onClick={() =>
            reject.mutate(connection.id, { onSuccess: () => setOutcome('declined') })
          }
        >
          {t('requests.decline')}
        </IonButton>
        <IonButton
          expand="block"
          className="flex-1"
          disabled={isActing}
          onClick={() =>
            accept.mutate(connection.id, { onSuccess: () => setOutcome('accepted') })
          }
        >
          {t('requests.accept')}
        </IonButton>
      </div>
      {/* An out that isn't accept/decline — the request stays pending and can
          still be handled later from the activity center / Profile. */}
      <button
        type="button"
        onClick={goHome}
        disabled={isActing}
        className="mt-3 w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
      >
        {t('resolve.maybeLater')}
      </button>
    </>,
  );
};
